import React from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { BaseRouteParams } from 'hocs'
import { usePermission } from 'admin/hocs'
import { useHasFeatureFunc } from 'utils/useHasFeature'
import { useAppSelector } from 'store/hooks'
import { pathUtils } from 'utils/pathnameFormatter'
import { UnderlineNavigationTabs } from 'uiComponents/navigation/underlineNavigationTabs'
import InSectionTabsSlot from 'uiComponents/navigation/sectionTabsSlot'
import { useTranslation } from 'translations'
import { ORDER_PATHS } from './paths'

interface OrdersSection {
    name: string
    path: string
    activeKey: string
    shouldShow: boolean
}

export function useOrdersSections(): OrdersSection[] {
    const { t } = useTranslation()
    const { accountSlug } = useParams<BaseRouteParams>()
    const { hasPermission } = usePermission()
    const hasFeature = useHasFeatureFunc()
    const user = useAppSelector((state) => state.auth.user)

    const orders = `/account/${accountSlug}/orders`
    const isPartnerAdmin = hasPermission('partner_admin', accountSlug)
    const isReseller = !!user?.resellerId
    const oldPages = hasFeature('oldOrdersPages')
    const showChannels =
        hasFeature('ResellersFeature') || hasFeature('RetailersReservationCodes') || hasFeature('GoogleThingsToDo')

    return [
        {
            name: 'Orders (old)',
            path: `${orders}/transactions`,
            activeKey: '/orders/transactions',
            shouldShow: oldPages,
        },
        { name: 'Orders details (old)', path: `${orders}/details`, activeKey: '/orders/details', shouldShow: oldPages },
        {
            name: t('orders.sectionTabs.overview'),
            path: `${orders}/orders`,
            activeKey: '/orders/orders',
            shouldShow: true,
        },
        {
            name: 'Daily Schedule',
            path: `${orders}/daily_schedule`,
            activeKey: '/orders/daily_schedule',
            shouldShow: hasFeature('dailySchedule'),
        },
        {
            name: 'Order Disputes',
            path: `${orders}/paypal/disputes`,
            activeKey: '/orders/paypal/disputes',
            shouldShow: hasFeature('PaypalResolutionCenter') && isPartnerAdmin,
        },
        {
            name: hasFeature('show_new_cart_page') ? 'Pending Reservations' : 'Carts',
            path: pathUtils.populateParams(ORDER_PATHS.fullPaths.cartPage, { accountSlug }),
            activeKey: '/orders/pending-reservations',
            shouldShow: hasFeature('openCartView'),
        },
        {
            name: 'Channels',
            path: `${orders}/channels`,
            activeKey: '/orders/channels',
            shouldShow: showChannels && isPartnerAdmin,
        },
        {
            name: 'Capacity',
            path: `${orders}/capacity_report`,
            activeKey: '/orders/capacity_report',
            shouldShow: !isReseller && hasFeature('is_capacity_report_enabled'),
        },
    ].filter((section) => section.shouldShow)
}

function OrdersSectionTabs() {
    const location = useLocation()
    const history = useHistory()
    const sections = useOrdersSections()

    const tabs = sections.map((section) => ({
        name: section.name,
        current: location.pathname.includes(section.activeKey),
        href: section.path,
        onClick: () => history.push(section.path),
    }))

    if (tabs.length < 2) {
        return null
    }

    return (
        <InSectionTabsSlot>
            <UnderlineNavigationTabs tabs={tabs} />
        </InSectionTabsSlot>
    )
}

export default OrdersSectionTabs
